import React, { useContext } from 'react';
import { css } from '@emotion/react';
import { Badge, DataTable, Spinner, StatCard } from '@onward-delivery/design';
import { UserContext } from '@/components/App';
import { CorpAdminContext } from '@/components/CorporateUsers/store';
import { greens, greys, reds } from '@/styles/colors';
import {
    AccentBar,
    Card,
    CardBody,
    CardRow,
    PageSurface,
    Section,
    SectionHeader,
    SectionNote,
    SectionTitle,
    StrongCell,
} from '@/components/CorporateShipper/blocks';
import { useCoverageCapacity } from './hooks';
import { PRIMARY } from './utils';

const rowDividerCss = css`
    border-bottom: 1px solid ${greys.border};
    gap: 12px;

    &:last-of-type {
        border-bottom: 0;
    }
`;

const inlineCss = css`
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

const mutedCss = css`
    color: ${greys.primary};
    font-size: 12px;
`;

const Header = ({ title, note }) => (
    <SectionHeader>
        <AccentBar />
        <SectionTitle>{title}</SectionTitle>
        {note && <SectionNote>{note}</SectionNote>}
    </SectionHeader>
);

const Swatch = ({ color }) => (
    <span
        css={css`
            background: ${color};
            border-radius: 2px;
            flex-shrink: 0;
            height: 12px;
            width: 12px;
        `}
    />
);

const CoverageStatus = ({ summary }) => {
    const color = summary.isSubmitted ? greens.primary : reds.primary;
    const label = summary.isSubmitted ? '● submitted' : '● waiting on the carrier — nothing submitted yet';

    return (
        <span
            css={css`
                color: ${color};
                font-size: 12px;
                font-weight: 700;
                white-space: nowrap;
            `}
        >
            {label}
        </span>
    );
};

const ProfileList = ({ placements }) => {
    if (placements.length === 0) {
        return (
            <Card>
                <CardBody>
                    Not in any delivery profile yet — assign on the <strong>Map</strong> (sub-region sheet → + Add
                    Carrier). Until then dispatch can't see them.
                </CardBody>
            </Card>
        );
    }

    return (
        <Card>
            {placements.map((placement) => (
                <CardRow key={placement.delivery_profile_id} css={rowDividerCss}>
                    <div css={inlineCss}>
                        <StrongCell>{placement.name}</StrongCell>
                        {placement.roles.map((role) => (
                            <Badge key={role} variant={role === PRIMARY ? 'success' : 'default'} size="xs">
                                {role}
                            </Badge>
                        ))}
                    </div>
                    <div css={inlineCss}>
                        {placement.shippers.length === 0 && <span css={mutedCss}>No shippers on this profile</span>}
                        {placement.shippers.map((shipper) => (
                            <Badge key={shipper.shipper_id} appearance="tinted" color={greens.primary} size="xs">
                                {shipper.business_name}
                            </Badge>
                        ))}
                    </div>
                </CardRow>
            ))}
        </Card>
    );
};

const RegionList = ({ regions }) => {
    if (regions.length === 0) {
        return (
            <Card>
                <CardBody>Not mapped or claimed in any of this organization's sub-regions yet.</CardBody>
            </Card>
        );
    }

    return (
        <Card>
            {regions.map((region) => {
                const swatchColor = region.claimed > 0 ? greens.primary : greys.border;
                const noneClaimed = region.claimed === 0 ? ' — none' : '';

                return (
                    <CardRow key={region.subregion_id} css={rowDividerCss}>
                        <div css={inlineCss}>
                            <Swatch color={swatchColor} />
                            <StrongCell>{region.name}</StrongCell>
                            <span css={mutedCss}>
                                · {region.claimed.toLocaleString()} of {region.total.toLocaleString()} zips claimed
                                {noneClaimed} · {region.profileName}
                            </span>
                        </div>
                        <div css={inlineCss}>
                            {region.role && (
                                <Badge variant={region.role === PRIMARY ? 'success' : 'default'} size="xs">
                                    {region.role}
                                </Badge>
                            )}
                            <Badge appearance="tinted" color={greys.primary} size="xs">
                                carrier · view-only
                            </Badge>
                        </div>
                    </CardRow>
                );
            })}
        </Card>
    );
};

const CoverageCapacity = () => {
    const { client: carrier } = useContext(UserContext);
    const { state } = useContext(CorpAdminContext);
    const { coverage, error, resourceTable } = useCoverageCapacity(carrier.client_id, state.organizationId);

    if (error && !coverage) {
        return (
            <Card>
                <CardBody>Couldn't load coverage and capacity. Refresh to try again.</CardBody>
            </Card>
        );
    }

    if (!coverage) {
        return (
            <div className="onward-design">
                <div className="flex justify-center p-12">
                    <Spinner />
                </div>
            </div>
        );
    }

    const { summary } = coverage;
    const zipValue = summary.zipCount > 0 ? summary.zipCount.toLocaleString() : '— none yet';
    const resourceValue = summary.resourceCount > 0 ? summary.resourceCount : '—';

    return (
        <div className="onward-design">
            <PageSurface>
                <Section>
                    <Header
                        title="Delivery profiles & shippers"
                        note="— synced from the Map — where this carrier is ranked, and which shippers that puts them in front of"
                    />
                    <ProfileList placements={coverage.placements} />
                </Section>

                <div className="grid grid-cols-3 gap-4">
                    <StatCard variant="metric" label="Zip codes claimed" value={zipValue} />
                    <StatCard variant="metric" label="Sub-regions touched" value={summary.subregionsTouched} />
                    <StatCard variant="metric" label="Resources" value={resourceValue} />
                </div>

                <Section>
                    <div
                        css={css`
                            align-items: center;
                            display: flex;
                            gap: 16px;
                            justify-content: space-between;
                        `}
                    >
                        <Header
                            title={`Coverage — submitted by ${carrier.business_name}`}
                            note="— view-only — claimed zip-by-zip in the carrier's own login"
                        />
                        <CoverageStatus summary={summary} />
                    </div>
                    <RegionList regions={coverage.regions} />
                </Section>

                <Section>
                    <Header title="Capacity — resources" note="— view-only — set up by the carrier on Resources" />
                    <DataTable table={resourceTable} emptyState="No resources yet" />
                </Section>
            </PageSurface>
        </div>
    );
};

export default CoverageCapacity;
